import {useMemo, useState} from "react";
import {PopupContainer} from "@FRAMEWORK/ui/desktop/popup";
import SwiperCore, {Pagination, Navigation, Thumbs} from "swiper";
import {SwiperSlide} from "swiper/react";
import {ZoomSlider, ThumbsSlider, ZoomSlideImg, ThumbsSlideImg} from "@COMPONENTS/style/slide";
import SlideWrap from "@COMPONENTS/style/detailpackage/detailimage";
import {PaginationOptions} from "swiper/types";
import ReactPlayer from "react-player";
import {OverlayState} from "@/hooks/common/useOverlay";
import {GoodsImage} from "@QUERIES/prdt/goods/useGoodsImage";
import {GoodsVideo} from "@QUERIES/prdt/goods/useGoodsVideo";
import {EventInfoImage} from "@QUERIES/prdt/event/useEventInfoImages";
import {EventInfoVideo} from "@QUERIES/prdt/event/useEventInfoVideos";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {Noimage} from "@yb/ui/desktop/common/Noimage";

interface Params {
	images: GoodsImage[] | EventInfoImage[];
	videos?: GoodsVideo[] | EventInfoVideo[];
	title: string;
}

const ImageVideoPopComponent = (props: OverlayState & Params) => {
	const {title, images, videos, ...overlayProps} = props;

	const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore>(null);
	const PaginationParams: PaginationOptions = {
		type: "fraction",
		formatFractionCurrent(number) {
			if (number < 10) {
				return `0${number}`;
			} else {
				return number;
			}
		},
		formatFractionTotal(number) {
			if (number < 10) {
				return `0${number}`;
			} else {
				return number;
			}
		},
	};

	const initialPlay = useMemo(() => Array.from({length: videos.length}, () => false), [videos]);
	const [playing, setPlaying] = useState(initialPlay);
	const onPause = () => {
		setPlaying(initialPlay);
	};

	const onPlaying = (index: number, status: boolean) => {
		const copy = [...playing];
		copy[index] = status;
		setPlaying(copy);
	};

	return (
		<PopupContainer {...overlayProps} title={title} size="regular">
			<SlideWrap>
				<ZoomSlider
					loop={true}
					navigation={true}
					pagination={PaginationParams}
					thumbs={{swiper: thumbsSwiper}}
					modules={[Pagination, Navigation, Thumbs]}
					initialSlide={0}
					onSwiper={() => setPlaying(Array.from({length: videos.length}, () => true))}
					onSlideChange={onPause}
				>
					{videos?.map((item, index) => (
						<SwiperSlide key={index}>
							<ZoomSlideImg types="movie">
								<ReactPlayer
									url={item.vimeoUrl}
									width={720}
									height={495}
									playing={playing[index]}
									onPlay={() => onPlaying(index, true)}
									onPause={() => onPlaying(index, false)}
									volume={0}
									muted={true}
									controls
								/>
							</ZoomSlideImg>
						</SwiperSlide>
					))}
					{images.map((item, index) => (
						<SwiperSlide key={index + videos.length}>
							<ZoomSlideImg>
								<Noimage width="720px" height="495px" src={item.imageThum4} alt={item.imageNote} />
							</ZoomSlideImg>
						</SwiperSlide>
					))}
				</ZoomSlider>
				<ThumbsSlider
					spaceBetween={10}
					slidesPerView={5}
					onSwiper={setThumbsSwiper}
					modules={[Navigation, Thumbs]}
					observer={true}
				>
					{videos?.map((item, index) => (
						<SwiperSlide key={index}>
							<ThumbsSlideImg types="movie">
								<Noimage width="136px" height="90px" src={item.vimeoThum1} alt={item.vimeoNote} />
							</ThumbsSlideImg>
						</SwiperSlide>
					))}
					{images.map((item, index) => (
						<SwiperSlide key={index + videos.length}>
							<ThumbsSlideImg>
								<Noimage width="136px" height="90px" src={item.imageThum4} alt={item.imageNote} />
							</ThumbsSlideImg>
						</SwiperSlide>
					))}
				</ThumbsSlider>
			</SlideWrap>
		</PopupContainer>
	);
};

const ImageVideoPop = (props: OverlayState & Params) => (
	<ErrorBoundary logMsg="Popup ImageVideoPop Error">
		<ImageVideoPopComponent {...props} />
	</ErrorBoundary>
);

export default ImageVideoPop;
